import { Button } from '@/components/ui/button'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { advanceFromKbdField } from '@/hooks/formKeyboardNav'
import { cn } from '@/lib/utils'
import { Check, ChevronDown, Plus, X } from 'lucide-react'
import type React from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'

export interface ComboboxOption {
	value: string
	label: string
	disabled?: boolean
	icon?: React.ReactNode
}

interface ComboboxProps {
	value: string
	onValueChange: (value: string) => void
	options: ComboboxOption[]
	placeholder?: string
	searchPlaceholder?: string
	emptyMessage?: string
	className?: string
	disabled?: boolean
	/** Values that should NOT close the dropdown when selected */
	keepOpenValues?: string[]
	/** When true, allows the user to type a value not in the options list */
	creatable?: boolean
	/** When true, shows a clear (X) button to reset the value while a value is set */
	clearable?: boolean
	/** Label shown for the create option. Defaults to `Create "${query}"` */
	creatableLabel?: (query: string) => string
	/**
	 * Creatable: block creating the typed value by returning a message explaining why (e.g. "attach
	 * already in progress"). The create row is hidden and the message shown in its place.
	 */
	createBlockedMessage?: (query: string) => string | undefined
	/** Unique ID for the combobox trigger, used for label association */
	id?: string
	/** Remote-search: turns off cmdk filtering; caller fetches and supplies matching `options`. */
	onSearchChange?: (search: string) => void
	/** Loading indicator in the list (remote-search). */
	loading?: boolean
	/** Called when the list scrolls near its end — load the next page (remote pagination). */
	onReachEnd?: () => void
	/** Trailing loading row while the next page loads (remote pagination). */
	loadingMore?: boolean
	/**
	 * Keep the dropdown inside the enclosing dialog (flips up when space below within the dialog runs out).
	 * Set false to constrain to the viewport instead, so the dropdown opens downward over the dialog.
	 */
	constrainToDialog?: boolean
	'aria-label'?: string
	'aria-invalid'?: boolean | 'true' | 'false'
	'data-kbd-field'?: string
	'data-testid'?: string
}

export function Combobox({
	value,
	onValueChange,
	options,
	placeholder = 'Select option...',
	searchPlaceholder = 'Search...',
	emptyMessage = 'No results found.',
	className,
	disabled = false,
	keepOpenValues = [],
	creatable = false,
	clearable = false,
	creatableLabel = (query: string) => `Create "${query}"`,
	createBlockedMessage,
	id,
	onSearchChange,
	loading = false,
	onReachEnd,
	loadingMore = false,
	constrainToDialog = true,
	'aria-label': ariaLabel,
	'aria-invalid': ariaInvalid,
	'data-kbd-field': dataKbdField,
	'data-testid': dataTestId,
}: ComboboxProps) {
	const [open, setOpen] = useState(false)
	const [search, setSearch] = useState('')
	const triggerRef = useRef<HTMLButtonElement>(null)
	// When the combobox lives inside a dialog, keep the dropdown within the modal (flip up instead of
	// spilling into the space below it). Falls back to the viewport when not in a dialog.
	const [collisionBoundary, setCollisionBoundary] = useState<HTMLElement | null>(null)

	const handleOpenChange = useCallback(
		(next: boolean) => {
			if (next) {
				setCollisionBoundary(
					constrainToDialog ? (triggerRef.current?.closest<HTMLElement>('[role="dialog"]') ?? null) : null,
				)
			}
			setOpen(next)
		},
		[constrainToDialog],
	)

	// Remote-search mode: cmdk must not client-filter (caller/server already scoped `options`).
	const isRemoteSearch = onSearchChange !== undefined
	const updateSearch = useCallback(
		(next: string) => {
			setSearch(next)
			onSearchChange?.(next)
		},
		[onSearchChange],
	)

	// Close the dropdown when the surrounding page/modal scrolls, so it can't stay floating detached from
	// its (now scrolled-away) trigger. Scrolling within the dropdown's own list is ignored.
	useEffect(() => {
		if (!open) {
			return
		}
		const handleScroll = (event: Event) => {
			const target = event.target
			if (target instanceof Element && target.closest('[data-slot="popover-content"]')) {
				return
			}
			setOpen(false)
		}
		document.addEventListener('scroll', handleScroll, true)
		return () => document.removeEventListener('scroll', handleScroll, true)
	}, [open])

	const scheduleAdvanceAfterSelect = () => {
		if (dataKbdField === undefined) {
			return
		}
		requestAnimationFrame(() => {
			if (triggerRef.current !== null) {
				advanceFromKbdField(triggerRef.current)
			}
		})
	}

	const selectedOption = options.find(option => option.value === value)
	// Display label from options if found, otherwise show the value itself (for cases where value exists but not in current options)
	const displayLabel = selectedOption ? selectedOption.label : value || placeholder
	const hasValue = selectedOption || value

	// A created (typed) value isn't in the options list; surface it as a selected row so reopening
	// the dropdown still shows what's picked.
	const createdValueSelected = creatable && value && !selectedOption

	const trimmedSearch = search.trim()
	const createBlocked = creatable && trimmedSearch ? createBlockedMessage?.(trimmedSearch) : undefined
	const showCreateOption =
		creatable &&
		trimmedSearch &&
		!createBlocked &&
		!options.some(o => o.value.toLowerCase() === trimmedSearch.toLowerCase())

	// Remote pagination: ask for the next page when the list scrolls near its bottom.
	const handleListScroll = useCallback(
		(e: React.UIEvent<HTMLDivElement>) => {
			if (!onReachEnd) {
				return
			}
			const el = e.currentTarget
			if (el.scrollTop + el.clientHeight >= el.scrollHeight - 48) {
				onReachEnd()
			}
		},
		[onReachEnd],
	)

	return (
		<Popover open={open} onOpenChange={handleOpenChange}>
			<PopoverTrigger asChild>
				<Button
					ref={triggerRef}
					id={id}
					variant='outline'
					data-kbd-field={dataKbdField}
					data-testid={dataTestId}
					// biome-ignore lint/a11y/useSemanticElements: ARIA combobox role required for accessible search dropdown
					role='combobox'
					aria-expanded={open}
					aria-haspopup='listbox'
					aria-controls='combobox-options'
					aria-label={ariaLabel}
					disabled={disabled}
					aria-invalid={ariaInvalid}
					className={cn(
						"border-input data-placeholder:text-muted-foreground [&_svg:not([class*='text-'])]:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid-style flex w-full items-center justify-between gap-2 rounded-md border bg-card px-3 py-2 text-body whitespace-nowrap shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 h-(--height-control) overflow-hidden min-w-0",
						!hasValue && 'text-muted-foreground',
						className,
					)}>
					<span className='truncate flex items-center gap-icon-text'>
						{selectedOption?.icon}
						{displayLabel}
					</span>
					{clearable && hasValue && !disabled ? (
						<span
							// biome-ignore lint/a11y/useSemanticElements: nested <button> is invalid inside the trigger button
							role='button'
							tabIndex={0}
							aria-label='Clear'
							data-testid={dataTestId ? `${dataTestId}-clear` : undefined}
							className='shrink-0 rounded-sm opacity-50 hover:opacity-100 focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none'
							onPointerDown={e => {
								// Prevent the popover trigger from toggling open.
								e.preventDefault()
								e.stopPropagation()
							}}
							onClick={e => {
								e.preventDefault()
								e.stopPropagation()
								onValueChange('')
								updateSearch('')
							}}
							onKeyDown={e => {
								if (e.key === 'Enter' || e.key === ' ') {
									e.preventDefault()
									e.stopPropagation()
									onValueChange('')
									updateSearch('')
								}
							}}>
							<X className='size-(--icon-standalone)' />
						</span>
					) : (
						<ChevronDown className='size-(--icon-standalone) shrink-0 opacity-50' />
					)}
				</Button>
			</PopoverTrigger>
			<PopoverContent
				data-testid={dataTestId ? `${dataTestId}-popover` : undefined}
				className='p-0 z-10000'
				style={{
					width: 'var(--radix-popover-trigger-width)',
				}}
				align='start'
				collisionBoundary={collisionBoundary ?? undefined}
				collisionPadding={8}
				// Hide (and our scroll handler closes) the dropdown once the trigger scrolls out of its
				// scroll container, so it can't float detached below the modal.
				hideWhenDetached
				onWheel={e => {
					e.stopPropagation()
				}}>
				<Command shouldFilter={!isRemoteSearch}>
					<CommandInput
						placeholder={searchPlaceholder}
						value={search}
						onValueChange={updateSearch}
						onClear={() => updateSearch('')}
						data-testid={dataTestId ? `${dataTestId}-search` : undefined}
					/>
					{/* Using ARIA listbox pattern instead of native <select> to support search/filter functionality */}
					<CommandList
						id='combobox-options'
						// biome-ignore lint/a11y/useSemanticElements: <explanation>
						role='listbox'
						onScroll={handleListScroll}
						className='max-h-60 [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-border [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-muted-foreground/50'>
						{!(loading || showCreateOption || createBlocked) && <CommandEmpty>{emptyMessage}</CommandEmpty>}
						{loading && <output className='block py-4 text-center text-body text-muted-foreground'>Searching…</output>}
						{/* Own row, not CommandEmpty: the message is about the typed value, so it must show even
						    when other options still match the search (e.g. typed "370" matching option "3700"). */}
						{createBlocked && (
							<output className='block py-4 px-2 text-center text-body text-destructive'>{createBlocked}</output>
						)}
						<CommandGroup>
							{/* Create row stays first: lazy-loaded pages keep appending options, so rendering it
							    last pushes it further down (often out of view) with every page. */}
							{showCreateOption && (
								<CommandItem
									value={trimmedSearch}
									onSelect={() => {
										onValueChange(trimmedSearch)
										updateSearch('')
										setOpen(false)
										scheduleAdvanceAfterSelect()
									}}
									className='cursor-pointer'>
									<Plus className='size-(--icon-standalone) mr-1' />
									{creatableLabel(trimmedSearch)}
								</CommandItem>
							)}
							{createdValueSelected && (
								<CommandItem
									value={value}
									// biome-ignore lint/a11y/useSemanticElements: ARIA role required for combobox pattern
									role='option'
									aria-selected
									onSelect={() => {
										updateSearch('')
										setOpen(false)
										scheduleAdvanceAfterSelect()
									}}
									className='cursor-pointer'>
									<span className='truncate'>{value}</span>
									<Check className='ml-auto size-(--icon-standalone) opacity-100' />
								</CommandItem>
							)}
							{options.map(option => (
								<CommandItem
									key={option.value}
									value={option.label}
									// biome-ignore lint/a11y/useSemanticElements: ARIA role required for combobox pattern
									role='option'
									aria-selected={value === option.value}
									disabled={option.disabled}
									onSelect={() => {
										if (!option.disabled) {
											onValueChange(option.value)
											updateSearch('')
											// Keep open if value is in keepOpenValues array
											if (!keepOpenValues.includes(option.value)) {
												setOpen(false)
												scheduleAdvanceAfterSelect()
											}
										}
									}}
									className={cn('cursor-pointer', option.disabled && 'opacity-50 cursor-not-allowed')}>
									<span className='flex items-center gap-icon-text'>
										{option.icon}
										{option.label}
									</span>
									<Check
										className={cn(
											'ml-auto size-(--icon-standalone)',
											value === option.value ? 'opacity-100' : 'opacity-0',
										)}
									/>
								</CommandItem>
							))}
						</CommandGroup>
						{loadingMore && (
							<output className='block py-2 text-center text-body text-muted-foreground'>Loading more…</output>
						)}
					</CommandList>
				</Command>
			</PopoverContent>
		</Popover>
	)
}
